<template>
  <div class="app-status-stack" aria-live="polite">
    <!-- نسخه‌ی جدید در دسترس است -->
    <transition name="status-slide">
      <div v-if="updateReady" class="status-bar status-bar--update" role="status">
        <span class="status-bar__icon"><i class="fas fa-sync-alt"></i></span>
        <span class="status-bar__text">نسخه‌ی جدیدی از پنل آماده است.</span>
        <span class="status-bar__actions">
          <button type="button" class="status-btn status-btn--solid" @click="applyUpdate">
            به‌روزرسانی
          </button>
          <button type="button" class="status-btn status-btn--ghost" @click="updateReady = false">
            بعداً
          </button>
        </span>
      </div>
    </transition>

    <!-- آفلاین -->
    <transition name="status-slide">
      <div v-if="showOffline" class="status-bar status-bar--offline" role="alert">
        <span class="status-bar__icon"><i class="fas fa-wifi status-icon-slash"></i></span>
        <span class="status-bar__text">
          <b>اتصال اینترنت قطع است.</b>
          <span class="status-bar__hint">آخرین اطلاعات ذخیره‌شده نمایش داده می‌شود؛ برای هماهنگ‌سازی دوباره آنلاین شوید.</span>
        </span>
        <span class="status-bar__actions">
          <button type="button" class="status-btn status-btn--solid" :disabled="checking" @click="retry">
            <i v-if="checking" class="fas fa-spinner fa-spin"></i>
            تلاش دوباره
          </button>
        </span>
      </div>
    </transition>

    <!-- بازگشت اتصال -->
    <transition name="status-slide">
      <div v-if="justReconnected" class="status-bar status-bar--online" role="status">
        <span class="status-bar__icon"><i class="fas fa-check"></i></span>
        <span class="status-bar__text">دوباره آنلاین شدید.</span>
      </div>
    </transition>

    <!-- نصب اپلیکیشن (Android/Desktop) -->
    <transition name="status-slide">
      <div v-if="showInstall" class="status-bar status-bar--install" role="region" aria-label="نصب اپلیکیشن">
        <span class="status-bar__icon"><i class="fas fa-arrow-alt-circle-down"></i></span>
        <span class="status-bar__text">
          <b>این پنل را به‌صورت اپلیکیشن نصب کنید.</b>
          <span class="status-bar__hint">دسترسی سریع‌تر، اجرای تمام‌صفحه و کارکرد در حالت آفلاین.</span>
        </span>
        <span class="status-bar__actions">
          <button type="button" class="status-btn status-btn--solid" :disabled="installing" @click="install">
            <i v-if="installing" class="fas fa-spinner fa-spin"></i>
            نصب
          </button>
          <button type="button" class="status-btn status-btn--ghost" @click="dismissInstall">
            نه، ممنون
          </button>
        </span>
      </div>
    </transition>

    <!-- راهنمای نصب روی iOS (بدون beforeinstallprompt) -->
    <transition name="status-slide">
      <div v-if="showIosHint" class="status-bar status-bar--install" role="region" aria-label="نصب روی iOS">
        <span class="status-bar__icon"><i class="fas fa-external-link-alt"></i></span>
        <span class="status-bar__text">
          <b>افزودن به صفحه‌ی اصلی</b>
          <span class="status-bar__hint">در Safari: دکمه‌ی «اشتراک‌گذاری» ← «Add to Home Screen».</span>
        </span>
        <span class="status-bar__actions">
          <button type="button" class="status-btn status-btn--ghost" @click="dismissInstall">
            متوجه شدم
          </button>
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import { networkState, recheck } from "@/utils/network";
import {
  pwaState,
  promptInstall,
  isIosSafari,
  isBannerDismissed,
  dismissBanner,
} from "@/utils/pwaInstall";

export default {
  name: "AppStatusBar",
  data() {
    return {
      updateReady: false,
      checking: false,
      installing: false,
      justReconnected: false,
      installDismissed: isBannerDismissed(),
    };
  },
  computed: {
    online() {
      return networkState.online;
    },
    showOffline() {
      return !networkState.online;
    },
    showInstall() {
      return (
        pwaState.canInstall &&
        !pwaState.installed &&
        !this.installDismissed &&
        networkState.online
      );
    },
    showIosHint() {
      return (
        !pwaState.installed &&
        !pwaState.canInstall &&
        !this.installDismissed &&
        isIosSafari() &&
        networkState.online
      );
    },
  },
  watch: {
    online(now, before) {
      if (now && before === false) {
        this.justReconnected = true;
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = setTimeout(() => {
          this.justReconnected = false;
        }, 3500);
      }
    },
  },
  created() {
    if (this.$workbox) {
      // رویداد waiting فقط وقتی شلیک می‌شود که واقعاً یک سرویس‌ورکرِ جدید نصب و
      // منتظر فعال‌سازی باشد → یعنی نسخه‌ی جدیدِ واقعی وجود دارد. تا وقتی کاربر
      // «به‌روزرسانی» را نزده، این پیام در هر بارگذاری درست است و باید دیده شود.
      this.$workbox.addEventListener("waiting", () => {
        this.updateReady = true;
      });
    }
  },
  beforeUnmount() {
    clearTimeout(this._reconnectTimer);
  },
  methods: {
    retry() {
      if (this.checking) return;
      this.checking = true;
      Promise.resolve(recheck()).finally(() => {
        this.checking = false;
      });
    },
    async applyUpdate() {
      this.updateReady = false;
      if (this.$workbox) {
        await this.$workbox.messageSW({ type: "SKIP_WAITING" });
      }
    },
    async install() {
      if (this.installing) return;
      this.installing = true;
      const outcome = await promptInstall();
      this.installing = false;
      if (outcome === "accepted") {
        if (this.$toastr) this.$toastr.s("در حال نصب اپلیکیشن…", "نصب");
      } else if (outcome === "dismissed") {
        this.dismissInstall();
      }
    },
    dismissInstall() {
      this.installDismissed = true;
      dismissBanner();
    },
  },
};
</script>

<style scoped>
.app-status-stack {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100002;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0;
  padding-top: calc(8px + env(safe-area-inset-top, 0px));
  pointer-events: none;
}

.status-bar {
  pointer-events: auto;
  width: 100%;
  max-width: 720px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #fff;
  background: #2b2f4a;
  box-shadow: 0 10px 30px -8px rgba(0, 0, 0, 0.45);
  -webkit-backdrop-filter: saturate(1.4) blur(6px);
  backdrop-filter: saturate(1.4) blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.status-bar__icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  font-size: 14px;
}

.status-bar__text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.status-bar__hint {
  font-size: 12px;
  opacity: 0.82;
  font-weight: 300;
}

.status-bar__actions {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  align-items: center;
}

.status-btn {
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.12s ease, background-color 0.12s ease, opacity 0.12s ease;
}
.status-btn:active {
  transform: translateY(1px);
}
.status-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.status-btn--solid {
  background: #fff;
  color: #23263e;
}
.status-btn--solid:hover:not(:disabled) {
  background: #eef0ff;
}
.status-btn--ghost {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.status-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* گونه‌ها */
.status-bar--update {
  background: linear-gradient(135deg, #3b4bb5, #5a3ea8);
}
.status-bar--offline {
  background: linear-gradient(135deg, #8a2b2b, #5f1f2e);
}
.status-bar--online {
  background: linear-gradient(135deg, #1f7a4d, #216b52);
}
.status-bar--install {
  background: linear-gradient(135deg, #23263e, #2f3352);
}

.status-icon-slash {
  position: relative;
}
.status-icon-slash::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 118%;
  height: 2px;
  background: currentColor;
  transform: translate(-50%, -50%) rotate(45deg);
  border-radius: 2px;
}

/* انیمیشن ورود/خروج */
.status-slide-enter-active,
.status-slide-leave-active {
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease;
}
.status-slide-enter-from,
.status-slide-leave-to {
  transform: translateY(-16px);
  opacity: 0;
}

@media (max-width: 575px) {
  .status-bar {
    flex-wrap: wrap;
    border-radius: 10px;
  }
  .status-bar__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
